import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  BarChart3,
  TrendingUp,
  Users,
  DollarSign,
  BookOpen,
  Target,
  Calendar,
  Loader2,
  ArrowUpRight,
  ArrowDownRight,
  Award,
  Building,
  Mic,
  RefreshCw,
  ShieldAlert,
  Repeat,
  ShoppingCart,
  UserCheck,
} from 'lucide-react';
import { adminAPI } from '@/services/api';
import { toast } from 'sonner';

export default function AdminAnalyticsPage() {
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('30');
  const [summary, setSummary] = useState(null);
  const [revenueData, setRevenueData] = useState(null);
  const [registrationData, setRegistrationData] = useState(null);
  const [userAnalytics, setUserAnalytics] = useState(null);
  const [topPerformers, setTopPerformers] = useState(null);
  const [clvData, setClvData] = useState(null);
  const [aovData, setAovData] = useState(null);
  const [funnelData, setFunnelData] = useState(null);
  const [stripeRefunds, setStripeRefunds] = useState(null);

  useEffect(() => {
    fetchAllAnalytics();
  }, [period]);

  const fetchAllAnalytics = async () => {
    setLoading(true);
    try {
      const [summaryRes, revenueRes, regRes, userRes, topRes, clvRes, aovRes, funnelRes, refundsRes] = await Promise.all([
        adminAPI.get('/admin/analytics/summary'),
        adminAPI.get(`/admin/analytics/revenue?days=${period}`),
        adminAPI.get(`/admin/analytics/registrations?days=${period}`),
        adminAPI.get(`/admin/analytics/users?days=${period}`),
        adminAPI.get('/admin/analytics/top-performers'),
        adminAPI.get('/admin/analytics/clv?days=365'),
        adminAPI.get(`/admin/analytics/aov?days=${period}`),
        adminAPI.get(`/admin/analytics/cart-abandonment?days=${period}`),
        adminAPI.get(`/admin/analytics/stripe-refunds?days=${period}`),
      ]);
      setSummary(summaryRes.data);
      setRevenueData(revenueRes.data);
      setRegistrationData(regRes.data);
      setUserAnalytics(userRes.data);
      setTopPerformers(topRes.data);
      setClvData(clvRes.data);
      setAovData(aovRes.data);
      setFunnelData(funnelRes.data);
      setStripeRefunds(refundsRes.data);
    } catch (error) {
      toast.error('Failed to load analytics data');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) =>
    new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(amount || 0);

  const formatCurrencyDecimal = (amount) =>
    new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2 }).format(amount || 0);

  const StatCard = ({ title, value, subValue, icon: Icon, color, trend }) => (
    <Card className="border-slate-200 rounded-xl hover:shadow-md transition-shadow" data-testid={`stat-card-${title.toLowerCase().replace(/\s+/g, '-')}`}>
      <CardContent className="p-5">
        <div className="flex items-start justify-between">
          <div className="space-y-1.5">
            <p className="text-xs font-medium text-slate-500 uppercase tracking-wide">{title}</p>
            <p className="text-2xl font-bold text-navy-700">{value}</p>
            {subValue && (
              <div className="flex items-center gap-1 text-xs">
                {trend === 'up' ? <ArrowUpRight className="w-3 h-3 text-green-500" /> : trend === 'down' ? <ArrowDownRight className="w-3 h-3 text-red-500" /> : null}
                <span className={trend === 'up' ? 'text-green-600' : trend === 'down' ? 'text-red-600' : 'text-slate-500'}>{subValue}</span>
              </div>
            )}
          </div>
          <div className={`p-2.5 rounded-lg ${color}`}>
            <Icon className="w-5 h-5 text-white" />
          </div>
        </div>
      </CardContent>
    </Card>
  );

  const SimpleBarChart = ({ data, color = 'bg-navy-500' }) => {
    if (!data || data.length === 0) return <p className="text-slate-400 text-sm py-4 text-center">No data available</p>;
    const maxValue = Math.max(...data.map(d => d.revenue || d.count || 0));
    return (
      <div className="space-y-1.5">
        {data.slice(-10).map((item, idx) => (
          <div key={idx} className="flex items-center gap-2">
            <span className="text-xs text-slate-400 w-16 text-right font-mono">{item.date?.slice(-5)}</span>
            <div className="flex-1 h-5 bg-slate-100 rounded overflow-hidden">
              <div className={`h-full ${color} rounded transition-all duration-500`} style={{ width: `${maxValue > 0 ? ((item.revenue || item.count || 0) / maxValue) * 100 : 0}%` }} />
            </div>
            <span className="text-xs font-semibold text-navy-700 w-14 text-right">{item.revenue ? formatCurrency(item.revenue) : item.count}</span>
          </div>
        ))}
      </div>
    );
  };

  const FunnelStep = ({ step, index, isLast }) => {
    const widthPercent = step.conversion_rate || 0;
    const colors = ['bg-navy-600', 'bg-blue-500', 'bg-amber-500', 'bg-green-500'];
    return (
      <div className="mb-3" data-testid={`funnel-step-${index}`}>
        <div className="flex justify-between items-center mb-1">
          <span className="text-sm font-medium text-slate-700">{step.name}</span>
          <div className="flex items-center gap-2">
            <span className="text-sm font-bold text-navy-700">{step.count.toLocaleString()}</span>
            {index > 0 && <Badge variant="outline" className="text-xs">{step.conversion_rate}%</Badge>}
          </div>
        </div>
        <div className="h-8 bg-slate-100 rounded-lg overflow-hidden">
          <div className={`h-full ${colors[index % 4]} rounded-lg transition-all duration-700 flex items-center justify-end ${widthPercent > 0 ? 'pr-2' : ''}`} style={{ width: `${widthPercent > 0 ? Math.max(widthPercent, 5) : 0}%` }}>
            {widthPercent > 15 && <span className="text-xs text-white font-medium">{step.conversion_rate}%</span>}
          </div>
        </div>
        {!isLast && step.drop_off > 0 && (
          <p className="text-xs text-red-400 mt-0.5 text-right">-{step.drop_off}% drop-off</p>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-navy-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-analytics-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="font-heading text-2xl font-bold text-navy-700">Analytics Dashboard</h2>
          <p className="text-sm text-slate-500">Track your platform performance and insights</p>
        </div>
        <div className="flex items-center gap-3">
          <Select value={period} onValueChange={setPeriod}>
            <SelectTrigger className="w-36" data-testid="period-select">
              <Calendar className="w-4 h-4 mr-2 text-slate-400" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="7">Last 7 days</SelectItem>
              <SelectItem value="30">Last 30 days</SelectItem>
              <SelectItem value="90">Last 90 days</SelectItem>
              <SelectItem value="365">Last year</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" size="sm" onClick={fetchAllAnalytics} className="rounded-lg" data-testid="refresh-analytics-btn">
            <RefreshCw className="w-4 h-4" />
          </Button>
        </div>
      </div>

      <Tabs defaultValue="overview" className="space-y-6">
        <TabsList className="bg-slate-100 rounded-xl p-1" data-testid="analytics-tabs">
          <TabsTrigger value="overview" className="rounded-lg data-[state=active]:bg-white">Overview</TabsTrigger>
          <TabsTrigger value="customers" className="rounded-lg data-[state=active]:bg-white">Customers & CLV</TabsTrigger>
          <TabsTrigger value="funnel" className="rounded-lg data-[state=active]:bg-white">Sales Funnel</TabsTrigger>
          <TabsTrigger value="refunds" className="rounded-lg data-[state=active]:bg-white">Refunds & Disputes</TabsTrigger>
        </TabsList>

        {/* ===== OVERVIEW TAB ===== */}
        <TabsContent value="overview" className="space-y-6">
          {summary && (
            <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
              <StatCard title="Total Revenue" value={formatCurrency(summary.total_revenue)} subValue={`${formatCurrency(summary.revenue_period)} this period`} icon={DollarSign} color="bg-green-500" trend="up" />
              <StatCard title="Total Users" value={summary.total_users} subValue={`+${summary.new_users_period} new`} icon={Users} color="bg-blue-500" trend="up" />
              <StatCard title="Registrations" value={summary.total_registrations} subValue={`+${summary.new_registrations_period} this period`} icon={BookOpen} color="bg-purple-500" trend="up" />
              <StatCard title="Avg Order Value" value={aovData ? formatCurrencyDecimal(aovData.avg_order_value) : '$0'} subValue={aovData ? `${aovData.total_orders} orders` : ''} icon={ShoppingCart} color="bg-amber-500" />
              <StatCard title="Lead Conversion" value={`${summary.conversion_rate}%`} subValue={`${summary.total_leads} total leads`} icon={Target} color="bg-orange-500" />
            </div>
          )}

          {/* Charts Row */}
          <div className="grid lg:grid-cols-2 gap-6">
            <Card className="border-slate-200 rounded-xl">
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="flex items-center gap-2 text-base"><BarChart3 className="w-4 h-4 text-green-500" />Revenue Trend</CardTitle>
                    <CardDescription className="text-xs">Daily revenue</CardDescription>
                  </div>
                  {revenueData && <Badge className="bg-green-50 text-green-700 border-green-200">Total: {formatCurrency(revenueData.total)}</Badge>}
                </div>
              </CardHeader>
              <CardContent>{revenueData && <SimpleBarChart data={revenueData.data} color="bg-green-500" />}</CardContent>
            </Card>
            <Card className="border-slate-200 rounded-xl">
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="flex items-center gap-2 text-base"><TrendingUp className="w-4 h-4 text-purple-500" />Registrations</CardTitle>
                    <CardDescription className="text-xs">Daily webinar registrations</CardDescription>
                  </div>
                  {registrationData && <Badge className="bg-purple-50 text-purple-700 border-purple-200">Total: {registrationData.total}</Badge>}
                </div>
              </CardHeader>
              <CardContent>{registrationData && <SimpleBarChart data={registrationData.daily_data} color="bg-purple-500" />}</CardContent>
            </Card>
          </div>

          {/* Top Performers */}
          <div className="grid lg:grid-cols-3 gap-6">
            <Card className="border-slate-200 rounded-xl">
              <CardHeader className="pb-3"><CardTitle className="flex items-center gap-2 text-base"><Award className="w-4 h-4 text-amber-500" />Top Webinars by Revenue</CardTitle></CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {topPerformers?.top_webinars_by_revenue?.slice(0, 5).map((item, idx) => (
                    <div key={idx} className="flex items-center justify-between p-2.5 bg-slate-50 rounded-lg">
                      <div className="flex items-center gap-2">
                        <span className="w-5 h-5 rounded-full bg-navy-100 text-navy-600 flex items-center justify-center text-xs font-bold">{idx + 1}</span>
                        <span className="text-xs font-medium text-navy-700 truncate max-w-[140px]">{item.title}</span>
                      </div>
                      <span className="text-xs font-bold text-green-600">{formatCurrency(item.revenue)}</span>
                    </div>
                  ))}
                  {(!topPerformers?.top_webinars_by_revenue || topPerformers.top_webinars_by_revenue.length === 0) && <p className="text-slate-400 text-sm text-center py-4">No data</p>}
                </div>
              </CardContent>
            </Card>
            <Card className="border-slate-200 rounded-xl">
              <CardHeader className="pb-3"><CardTitle className="flex items-center gap-2 text-base"><Mic className="w-4 h-4 text-blue-500" />Top Speakers</CardTitle></CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {topPerformers?.top_speakers?.slice(0, 5).map((item, idx) => (
                    <div key={idx} className="flex items-center justify-between p-2.5 bg-slate-50 rounded-lg">
                      <div className="flex items-center gap-2">
                        <span className="w-5 h-5 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center text-xs font-bold">{idx + 1}</span>
                        <span className="text-xs font-medium text-navy-700 truncate max-w-[140px]">{item.name}</span>
                      </div>
                      <Badge className="bg-blue-50 text-blue-700 text-xs">{item.webinar_count}</Badge>
                    </div>
                  ))}
                  {(!topPerformers?.top_speakers || topPerformers.top_speakers.length === 0) && <p className="text-slate-400 text-sm text-center py-4">No data</p>}
                </div>
              </CardContent>
            </Card>
            <Card className="border-slate-200 rounded-xl">
              <CardHeader className="pb-3"><CardTitle className="flex items-center gap-2 text-base"><Building className="w-4 h-4 text-purple-500" />Top Industries</CardTitle></CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {topPerformers?.top_industries?.slice(0, 5).map((item, idx) => (
                    <div key={idx} className="flex items-center justify-between p-2.5 bg-slate-50 rounded-lg">
                      <div className="flex items-center gap-2">
                        <span className="w-5 h-5 rounded-full bg-purple-100 text-purple-600 flex items-center justify-center text-xs font-bold">{idx + 1}</span>
                        <span className="text-xs font-medium text-navy-700 truncate max-w-[140px]">{item.name}</span>
                      </div>
                      <Badge className="bg-purple-50 text-purple-700 text-xs">{item.webinar_count}</Badge>
                    </div>
                  ))}
                  {(!topPerformers?.top_industries || topPerformers.top_industries.length === 0) && <p className="text-slate-400 text-sm text-center py-4">No data</p>}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* User Analytics */}
          <div className="grid lg:grid-cols-2 gap-6">
            <Card className="border-slate-200 rounded-xl">
              <CardHeader className="pb-3"><CardTitle className="flex items-center gap-2 text-base"><Users className="w-4 h-4 text-blue-500" />Users by Role</CardTitle></CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {userAnalytics?.by_role && Object.entries(userAnalytics.by_role).map(([role, count]) => (
                    <div key={role} className="flex items-center justify-between">
                      <Badge className={role === 'super_admin' ? 'bg-red-50 text-red-700' : role === 'admin' ? 'bg-orange-50 text-orange-700' : role === 'speaker' ? 'bg-purple-50 text-purple-700' : 'bg-blue-50 text-blue-700'}>
                        {role.replace('_', ' ')}
                      </Badge>
                      <span className="text-xl font-bold text-navy-700">{count}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
            <Card className="border-slate-200 rounded-xl">
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-base"><BookOpen className="w-4 h-4 text-green-500" />Most Popular Webinars</CardTitle>
                <CardDescription className="text-xs">By registration count</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {registrationData?.top_webinars?.slice(0, 5).map((item, idx) => (
                    <div key={idx} className="flex items-center justify-between p-2.5 bg-slate-50 rounded-lg">
                      <div className="flex items-center gap-2">
                        <span className="w-5 h-5 rounded-full bg-green-100 text-green-600 flex items-center justify-center text-xs font-bold">{idx + 1}</span>
                        <span className="text-xs font-medium text-navy-700 truncate max-w-[180px]">{item.title}</span>
                      </div>
                      <Badge className="bg-green-50 text-green-700 text-xs">{item.count}</Badge>
                    </div>
                  ))}
                  {(!registrationData?.top_webinars || registrationData.top_webinars.length === 0) && <p className="text-slate-400 text-sm text-center py-4">No data</p>}
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* ===== CUSTOMERS & CLV TAB ===== */}
        <TabsContent value="customers" className="space-y-6">
          {clvData && (
            <>
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                <StatCard title="Avg CLV" value={formatCurrencyDecimal(clvData.avg_clv)} subValue={`${clvData.total_customers} customers`} icon={DollarSign} color="bg-green-500" />
                <StatCard title="Repeat Rate" value={`${clvData.repeat_rate}%`} subValue={`${clvData.repeat_buyers} repeat buyers`} icon={Repeat} color="bg-blue-500" />
                <StatCard title="One-Time Buyers" value={clvData.one_time_buyers.toLocaleString()} subValue={`${(100 - clvData.repeat_rate).toFixed(1)}% of total`} icon={UserCheck} color="bg-slate-500" />
                <StatCard title="Avg Orders/Customer" value={clvData.avg_orders_per_customer} icon={ShoppingCart} color="bg-purple-500" />
              </div>

              {/* CLV Distribution */}
              <Card className="border-slate-200 rounded-xl">
                <CardHeader className="pb-3">
                  <CardTitle className="text-base">Customer Lifetime Value Distribution</CardTitle>
                  <CardDescription className="text-xs">How much customers spend over their lifetime</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {clvData.distribution?.map((bucket, idx) => {
                      const maxCount = Math.max(...clvData.distribution.map(b => b.count));
                      const pct = maxCount > 0 ? (bucket.count / maxCount) * 100 : 0;
                      const colors = ['bg-slate-400', 'bg-blue-400', 'bg-purple-500', 'bg-amber-500', 'bg-green-500'];
                      return (
                        <div key={idx} className="flex items-center gap-3" data-testid={`clv-bucket-${idx}`}>
                          <span className="text-xs font-medium text-slate-600 w-20 text-right">{bucket.bucket}</span>
                          <div className="flex-1 h-7 bg-slate-100 rounded overflow-hidden">
                            <div className={`h-full ${colors[idx]} rounded transition-all duration-700 flex items-center pl-2`} style={{ width: `${Math.max(pct, 3)}%` }}>
                              {pct > 20 && <span className="text-xs text-white font-medium">{bucket.count}</span>}
                            </div>
                          </div>
                          <span className="text-xs font-bold text-navy-700 w-12 text-right">{bucket.count}</span>
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>

              {/* Top Customers */}
              <Card className="border-slate-200 rounded-xl">
                <CardHeader className="pb-3">
                  <CardTitle className="text-base">Top 10 Highest-Value Customers</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm" data-testid="top-customers-table">
                      <thead>
                        <tr className="border-b border-slate-200">
                          <th className="text-left py-2.5 px-3 text-xs font-medium text-slate-500 uppercase">#</th>
                          <th className="text-left py-2.5 px-3 text-xs font-medium text-slate-500 uppercase">Customer</th>
                          <th className="text-left py-2.5 px-3 text-xs font-medium text-slate-500 uppercase">Email</th>
                          <th className="text-right py-2.5 px-3 text-xs font-medium text-slate-500 uppercase">Orders</th>
                          <th className="text-right py-2.5 px-3 text-xs font-medium text-slate-500 uppercase">Total Spent</th>
                        </tr>
                      </thead>
                      <tbody>
                        {clvData.top_customers?.map((c, idx) => (
                          <tr key={idx} className="border-b border-slate-100 hover:bg-slate-50">
                            <td className="py-2.5 px-3"><span className="w-5 h-5 rounded-full bg-navy-100 text-navy-600 flex items-center justify-center text-xs font-bold">{idx + 1}</span></td>
                            <td className="py-2.5 px-3 font-medium text-navy-700">{c.name || 'N/A'}</td>
                            <td className="py-2.5 px-3 text-slate-500 text-xs">{c.email}</td>
                            <td className="py-2.5 px-3 text-right">{c.order_count}</td>
                            <td className="py-2.5 px-3 text-right font-bold text-green-600">{formatCurrencyDecimal(c.total_spent)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>
            </>
          )}
        </TabsContent>

        {/* ===== SALES FUNNEL TAB ===== */}
        <TabsContent value="funnel" className="space-y-6">
          <div className="grid lg:grid-cols-3 gap-4">
            {funnelData && (
              <>
                <StatCard title="Checkout Conversion" value={`${funnelData.steps?.[funnelData.steps.length - 1]?.conversion_rate ?? 0}%`} subValue="Checkout to payment" icon={Target} color="bg-green-500" trend="up" />
                <StatCard title="Cart Abandonment" value={`${funnelData.abandonment_rate}%`} subValue={`${funnelData.abandoned_count} abandoned`} icon={ShoppingCart} color="bg-red-500" trend="down" />
                <StatCard title="Completed Orders" value={funnelData.completed_count} subValue="in selected period" icon={DollarSign} color="bg-green-500" />
              </>
            )}
          </div>

          <Card className="border-slate-200 rounded-xl">
            <CardHeader className="pb-3">
              <CardTitle className="text-base">Purchase Funnel</CardTitle>
              <CardDescription className="text-xs">Drop-off at each stage of the buying process</CardDescription>
            </CardHeader>
            <CardContent>
              {funnelData?.steps?.map((step, idx) => (
                <FunnelStep key={idx} step={step} index={idx} isLast={idx === funnelData.steps.length - 1} />
              ))}
            </CardContent>
          </Card>

          {/* AOV Details */}
          {aovData && (
            <Card className="border-slate-200 rounded-xl">
              <CardHeader className="pb-3">
                <CardTitle className="text-base">Order Value Analysis</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="p-4 bg-slate-50 rounded-lg text-center">
                    <p className="text-xs text-slate-500 uppercase mb-1">Avg Order Value</p>
                    <p className="text-xl font-bold text-navy-700">{formatCurrencyDecimal(aovData.avg_order_value)}</p>
                  </div>
                  <div className="p-4 bg-slate-50 rounded-lg text-center">
                    <p className="text-xs text-slate-500 uppercase mb-1">Highest Order</p>
                    <p className="text-xl font-bold text-green-600">{formatCurrencyDecimal(aovData.max_order)}</p>
                  </div>
                  <div className="p-4 bg-slate-50 rounded-lg text-center">
                    <p className="text-xs text-slate-500 uppercase mb-1">Lowest Order</p>
                    <p className="text-xl font-bold text-amber-600">{formatCurrencyDecimal(aovData.min_order)}</p>
                  </div>
                  <div className="p-4 bg-slate-50 rounded-lg text-center">
                    <p className="text-xs text-slate-500 uppercase mb-1">Avg Items/Order</p>
                    <p className="text-xl font-bold text-navy-700">{aovData.avg_items_per_order}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* ===== REFUNDS & DISPUTES TAB ===== */}
        <TabsContent value="refunds" className="space-y-6">
          {stripeRefunds?.error ? (
            <Card className="border-slate-200 rounded-xl">
              <CardContent className="p-8 text-center">
                <ShieldAlert className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                <p className="text-slate-500">Stripe is not configured. Please add your Stripe Secret Key in Settings to view refund data.</p>
              </CardContent>
            </Card>
          ) : stripeRefunds?.summary ? (
            <>
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                <StatCard title="Total Refunds" value={stripeRefunds.summary.total_refunds} subValue={formatCurrencyDecimal(stripeRefunds.summary.total_refund_amount)} icon={RefreshCw} color="bg-red-500" />
                <StatCard title="Refund Rate" value={`${stripeRefunds.summary.refund_rate}%`} subValue={`of ${formatCurrency(stripeRefunds.summary.period_revenue)} revenue`} icon={Target} color="bg-amber-500" />
                <StatCard title="Disputes" value={stripeRefunds.summary.total_disputes} subValue={formatCurrencyDecimal(stripeRefunds.summary.total_dispute_amount)} icon={ShieldAlert} color="bg-orange-500" />
                <StatCard title="Chargeback Rate" value={`${stripeRefunds.summary.chargeback_rate}%`} icon={ShieldAlert} color="bg-red-600" />
              </div>

              {/* Refund Reasons */}
              {stripeRefunds.refund_reasons?.length > 0 && (
                <Card className="border-slate-200 rounded-xl">
                  <CardHeader className="pb-3"><CardTitle className="text-base">Refund Reasons</CardTitle></CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      {stripeRefunds.refund_reasons.map((r, idx) => (
                        <div key={idx} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                          <span className="text-sm font-medium text-slate-700 capitalize">{r.reason.replace(/_/g, ' ')}</span>
                          <Badge className="bg-red-50 text-red-700">{r.count}</Badge>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Recent Refunds Table */}
              {stripeRefunds.refunds?.length > 0 && (
                <Card className="border-slate-200 rounded-xl">
                  <CardHeader className="pb-3"><CardTitle className="text-base">Recent Refunds</CardTitle></CardHeader>
                  <CardContent>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm" data-testid="refunds-table">
                        <thead>
                          <tr className="border-b border-slate-200">
                            <th className="text-left py-2.5 px-3 text-xs font-medium text-slate-500 uppercase">Date</th>
                            <th className="text-left py-2.5 px-3 text-xs font-medium text-slate-500 uppercase">ID</th>
                            <th className="text-left py-2.5 px-3 text-xs font-medium text-slate-500 uppercase">Reason</th>
                            <th className="text-left py-2.5 px-3 text-xs font-medium text-slate-500 uppercase">Status</th>
                            <th className="text-right py-2.5 px-3 text-xs font-medium text-slate-500 uppercase">Amount</th>
                          </tr>
                        </thead>
                        <tbody>
                          {stripeRefunds.refunds.map((r, idx) => (
                            <tr key={idx} className="border-b border-slate-100 hover:bg-slate-50">
                              <td className="py-2.5 px-3 text-xs text-slate-500">{new Date(r.created).toLocaleDateString()}</td>
                              <td className="py-2.5 px-3 text-xs font-mono text-slate-600">{r.id.slice(0, 20)}...</td>
                              <td className="py-2.5 px-3 text-xs capitalize">{r.reason?.replace(/_/g, ' ')}</td>
                              <td className="py-2.5 px-3">
                                <Badge className={r.status === 'succeeded' ? 'bg-green-50 text-green-700' : 'bg-amber-50 text-amber-700'}>{r.status}</Badge>
                              </td>
                              <td className="py-2.5 px-3 text-right font-bold text-red-600">{formatCurrencyDecimal(r.amount)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Recent Disputes Table */}
              {stripeRefunds.disputes?.length > 0 && (
                <Card className="border-slate-200 rounded-xl">
                  <CardHeader className="pb-3"><CardTitle className="text-base">Recent Disputes / Chargebacks</CardTitle></CardHeader>
                  <CardContent>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm" data-testid="disputes-table">
                        <thead>
                          <tr className="border-b border-slate-200">
                            <th className="text-left py-2.5 px-3 text-xs font-medium text-slate-500 uppercase">Date</th>
                            <th className="text-left py-2.5 px-3 text-xs font-medium text-slate-500 uppercase">ID</th>
                            <th className="text-left py-2.5 px-3 text-xs font-medium text-slate-500 uppercase">Reason</th>
                            <th className="text-left py-2.5 px-3 text-xs font-medium text-slate-500 uppercase">Status</th>
                            <th className="text-right py-2.5 px-3 text-xs font-medium text-slate-500 uppercase">Amount</th>
                          </tr>
                        </thead>
                        <tbody>
                          {stripeRefunds.disputes.map((d, idx) => (
                            <tr key={idx} className="border-b border-slate-100 hover:bg-slate-50">
                              <td className="py-2.5 px-3 text-xs text-slate-500">{new Date(d.created).toLocaleDateString()}</td>
                              <td className="py-2.5 px-3 text-xs font-mono text-slate-600">{d.id.slice(0, 20)}...</td>
                              <td className="py-2.5 px-3 text-xs capitalize">{d.reason?.replace(/_/g, ' ')}</td>
                              <td className="py-2.5 px-3"><Badge className="bg-orange-50 text-orange-700">{d.status}</Badge></td>
                              <td className="py-2.5 px-3 text-right font-bold text-red-600">{formatCurrencyDecimal(d.amount)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </CardContent>
                </Card>
              )}

              {stripeRefunds.refunds?.length === 0 && stripeRefunds.disputes?.length === 0 && (
                <Card className="border-slate-200 rounded-xl">
                  <CardContent className="p-8 text-center">
                    <ShieldAlert className="w-12 h-12 text-green-300 mx-auto mb-3" />
                    <p className="text-lg font-semibold text-green-700">No Refunds or Disputes</p>
                    <p className="text-slate-500 text-sm mt-1">Your account has no refunds or chargebacks in the selected period.</p>
                  </CardContent>
                </Card>
              )}
            </>
          ) : null}
        </TabsContent>
      </Tabs>
    </div>
  );
}
